import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { toast } from "sonner";
import { Download, FileText, TrendingUp, ShieldCheck, User, BookOpen, BarChart3, Sparkles, Receipt } from "lucide-react";

const ICONS = {
  "daily-recon":        FileText,
  "revenue":            TrendingUp,
  "fx-exposure":        BarChart3,
  "compliance":         ShieldCheck,
  "customer-statement": User,
  "accounting":         BookOpen,
  "xpay-settlements":   Sparkles,
  "xpay-transactions":  Receipt,
};

const GROUP_LABELS = {
  consumer: "Consumer on-ramp",
  xpay:     "Xchangely Pay (B2B)",
};

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [fxData, setFxData] = useState(null);

  useEffect(() => {
    api.get("/admin/reports")
      .then((r) => setReports(r.data.reports || []))
      .catch(() => toast.error("Failed to load reports"))
      .finally(() => setLoading(false));
  }, []);

  const buildQs = () => {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to)   qs.set("to", to);
    return qs.toString();
  };

  const downloadCsv = async (r) => {
    let path = r.path;
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to)   qs.set("to", to);
    if (r.id === "customer-statement") {
      if (!customerId.trim()) { toast.error("Enter a customer ID first"); return; }
      qs.set("customer_id", customerId.trim());
    }
    if (r.group === "xpay" && merchantId.trim()) {
      qs.set("merchant_id", merchantId.trim());
    }
    if (qs.toString()) path += `?${qs.toString()}`;
    try {
      const token = localStorage.getItem("xch_token");
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiBase}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const t = await resp.text();
        toast.error(`Failed: ${resp.status} ${t.slice(0, 100)}`);
        return;
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = (resp.headers.get("Content-Disposition") || "").match(/filename="?([^"]+)"?/)?.[1] || `${r.id}.csv`;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) { toast.error("Download failed"); }
  };

  const loadFx = async () => {
    try {
      const qs = buildQs();
      const r = await api.get(`/admin/reports/fx-exposure${qs ? `?${qs}` : ""}`);
      setFxData(r.data);
    } catch (e) { toast.error("Failed to load FX exposure"); }
  };

  return (
    <AdminLayout
      title="Reports"
      subtitle="Exportable daily reconciliation, revenue split, FX exposure, AML, customer statement and accounting feeds."
    >
      <div className="xch-card p-4 mb-5" data-testid="reports-filters">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                   className="rounded-xl font-mono" data-testid="reports-from-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                   className="rounded-xl font-mono" data-testid="reports-to-input" />
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          Date range applies to every report. Defaults to last 30 days when blank.
          Reports that need extra inputs (customer / merchant id) show them on the card itself.
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-6">
          {["consumer", "xpay"].map((groupKey) => {
            const groupReports = reports.filter((r) => (r.group || "consumer") === groupKey);
            if (groupReports.length === 0) return null;
            return (
              <section key={groupKey} data-testid={`reports-group-${groupKey}`}>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="font-display font-bold text-base">{GROUP_LABELS[groupKey] || groupKey}</h2>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    · {groupReports.length} report{groupReports.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {groupReports.map((r) => {
                    const Icon = ICONS[r.id] || FileText;
                    const isFx = r.id === "fx-exposure";
                    const needsCustomerId = r.id === "customer-statement";
                    const supportsMerchantId = r.group === "xpay";
                    return (
                      <div key={r.id} className="xch-card p-5" data-testid={`report-${r.id}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-foreground text-base">{r.label}</div>
                            <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{r.desc}</div>
                          </div>
                        </div>

                        {/* Report-specific filters */}
                        {needsCustomerId && (
                          <div className="mt-3 space-y-1">
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                              Customer ID <span className="text-rose-600">*required</span>
                            </Label>
                            <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                                    placeholder="uuid"
                                    className="rounded-xl font-mono text-xs h-9"
                                    data-testid="reports-customer-id-input" />
                          </div>
                        )}
                        {supportsMerchantId && (
                          <div className="mt-3 space-y-1">
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                              Merchant ID <span className="text-muted-foreground/70">(optional — leave blank for all)</span>
                            </Label>
                            <Input value={merchantId} onChange={(e) => setMerchantId(e.target.value)}
                                    placeholder="uuid"
                                    className="rounded-xl font-mono text-xs h-9"
                                    data-testid={`reports-merchant-id-input-${r.id}`} />
                          </div>
                        )}

                        <div className="mt-3">
                          {isFx ? (
                            <div>
                              <Button onClick={loadFx} size="sm" variant="outline"
                                      className="rounded-xl gap-1.5" data-testid={`load-${r.id}-btn`}>
                                <BarChart3 className="w-3.5 h-3.5" /> Load report
                              </Button>
                              {fxData && (
                                <div className="mt-3 grid grid-cols-2 gap-2" data-testid="fx-data">
                                  <KV label="Realised FX P&L (EUR)" value={fxData.realised_fx_pnl_eur} />
                                  <KV label="Open FX exposure (EUR)" value={fxData.open_fx_exposure_eur} />
                                </div>
                              )}
                            </div>
                          ) : (
                            <Button onClick={() => downloadCsv(r)} size="sm"
                                    className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                                    data-testid={`download-${r.id}-btn`}>
                              <Download className="w-3.5 h-3.5" /> Download CSV
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

const KV = ({ label, value }) => (
  <div className="rounded-xl bg-muted/40 p-3">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <div className="font-mono text-lg font-bold text-foreground tabular-nums">
      {(value ?? 0).toLocaleString("en-IE", { maximumFractionDigits: 4 })}
    </div>
  </div>
);
